import React, { useEffect, useMemo, useState } from "react";
import { Close, FilterList } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

export type FilterableDataTableColumn<T> = {
  id: string;
  label: string;
  getValue?: (row: T) => string | number | null | undefined;
  render?: (row: T) => React.ReactNode;
  filter?: "text" | "select" | "none";
  filterOptions?: ReadonlyArray<{ label: string; value: string }>;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  minWidth?: number;
};

type FilterableDataTableProps<T> = {
  rows: T[];
  columns: ReadonlyArray<FilterableDataTableColumn<T>>;
  getRowId: (row: T) => string | number;
  ariaLabel: string;
  emptyMessage?: string;
  minWidth?: number;
};

const normalize = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .trim();

export function FilterableDataTable<T>({
  rows,
  columns,
  getRowId,
  ariaLabel,
  emptyMessage = "No hay resultados con estos filtros.",
  minWidth = 720,
}: FilterableDataTableProps<T>) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<{ column: string; direction: "asc" | "desc" }>({
    column: "",
    direction: "asc",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filterColumnId, setFilterColumnId] = useState<string | null>(null);

  const filteredRows = useMemo(() => {
    const matchingRows = rows.filter((row) =>
      columns.every((column) => {
        const filterValue = filters[column.id];
        if (!filterValue || column.filter === "none" || !column.getValue) return true;
        const cellValue = normalize(column.getValue(row));
        return column.filter === "select"
          ? cellValue === normalize(filterValue)
          : cellValue.includes(normalize(filterValue));
      })
    );

    if (!sort.column) return matchingRows;
    const sortColumn = columns.find((column) => column.id === sort.column);
    if (!sortColumn?.getValue) return matchingRows;

    return [...matchingRows].sort((left, right) => {
      const leftValue = sortColumn.getValue?.(left);
      const rightValue = sortColumn.getValue?.(right);
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue ?? "").localeCompare(String(rightValue ?? ""), "es", {
              numeric: true,
              sensitivity: "base",
            });
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [columns, filters, rows, sort]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const visibleRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const activeFilterColumn = columns.find((column) => column.id === filterColumnId);

  const updateFilter = (columnId: string, value: string) => {
    setFilters((current) => ({ ...current, [columnId]: value }));
    setPage(0);
  };

  const toggleSort = (columnId: string) => {
    setSort((current) => ({
      column: columnId,
      direction: current.column === columnId && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setPage(0);
  };

  return (
    <>
      {activeFilterCount > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredRows.length} resultados · {activeFilterCount} {activeFilterCount === 1 ? "filtro activo" : "filtros activos"}
          </Typography>
          <Button size="small" color="inherit" startIcon={<Close fontSize="small" />} onClick={clearFilters}>
            Limpiar filtros
          </Button>
        </Stack>
      )}
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth }} aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                return (
                  <TableCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                      {column.sortable === false || !column.getValue ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sort.column === column.id}
                          direction={sort.column === column.id ? sort.direction : "asc"}
                          onClick={() => toggleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                      {column.filter !== "none" && column.getValue ? (
                        <IconButton
                          size="small"
                          aria-label={`Filtrar ${column.label}`}
                          aria-haspopup="dialog"
                          aria-expanded={filterColumnId === column.id && Boolean(filterAnchor)}
                          color={filters[column.id] ? "primary" : "default"}
                          onClick={(event) => {
                            setFilterColumnId(column.id);
                            setFilterAnchor(event.currentTarget);
                          }}
                        >
                          <Badge variant="dot" color="primary" invisible={!filters[column.id]}>
                            <FilterList fontSize="small" />
                          </Badge>
                        </IconButton>
                      ) : null}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length ? (
              visibleRows.map((row) => (
                <TableRow hover key={getRowId(row)}>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.render
                        ? column.render(row)
                        : String(column.getValue?.(row) ?? "—")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={Boolean(filterAnchor && activeFilterColumn)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 300, maxWidth: "calc(100vw - 32px)", p: 2 } } }}
      >
        {activeFilterColumn ? (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              Filtrar por {activeFilterColumn.label.toLocaleLowerCase()}
            </Typography>
            {activeFilterColumn.filter === "select" && activeFilterColumn.filterOptions ? (
              <TextField
                select
                fullWidth
                size="small"
                label={activeFilterColumn.label}
                value={filters[activeFilterColumn.id] || ""}
                onChange={(event) => updateFilter(activeFilterColumn.id, event.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                {activeFilterColumn.filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                autoFocus
                fullWidth
                size="small"
                label={activeFilterColumn.label}
                placeholder={`Escribe para filtrar ${activeFilterColumn.label.toLocaleLowerCase()}`}
                value={filters[activeFilterColumn.id] || ""}
                onChange={(event) => updateFilter(activeFilterColumn.id, event.target.value)}
              />
            )}
            <Stack direction="row" justifyContent="space-between">
              <Button
                size="small"
                disabled={!filters[activeFilterColumn.id]}
                onClick={() => updateFilter(activeFilterColumn.id, "")}
              >
                Limpiar
              </Button>
              <Button size="small" variant="contained" onClick={() => setFilterAnchor(null)}>
                Listo
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Box />
        )}
      </Popover>
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </>
  );
}
